/**
 * Client-safe Web Push / Firebase web config from NEXT_PUBLIC_* env vars.
 * Safe to import from client components.
 */

export type FirebaseWebClientConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
};

function trimOrEmpty(value: string | undefined): string {
  return value?.trim() ?? "";
}

/**
 * Firebase JS SDK-style config for the browser. Returns null if any required field is missing.
 */
export function getFirebaseWebClientConfig(): FirebaseWebClientConfig | null {
  const apiKey = trimOrEmpty(process.env.NEXT_PUBLIC_FIREBASE_API_KEY);
  const authDomain = trimOrEmpty(process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN);
  const projectId = trimOrEmpty(process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID);
  const storageBucket = trimOrEmpty(
    process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  );
  const messagingSenderId = trimOrEmpty(
    process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  );
  const appId = trimOrEmpty(process.env.NEXT_PUBLIC_FIREBASE_APP_ID);
  const measurementId = trimOrEmpty(
    process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
  );

  if (
    !apiKey ||
    !authDomain ||
    !projectId ||
    !storageBucket ||
    !messagingSenderId ||
    !appId
  ) {
    return null;
  }

  const config: FirebaseWebClientConfig = {
    apiKey,
    authDomain,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
  };

  if (measurementId) {
    config.measurementId = measurementId;
  }

  return config;
}

/**
 * VAPID public key for PushManager.subscribe({ applicationServerKey }).
 * Expects the literal env value (URL-safe base64); decode to Uint8Array at subscribe time.
 */
export function getWebPushVapidPublicKeyBase64(): string | null {
  const key = trimOrEmpty(process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY);
  return key || null;
}

/**
 * True when Firebase web config and VAPID public key are both present (client push ready).
 */
export function isWebPushClientEnvComplete(): boolean {
  return (
    getFirebaseWebClientConfig() !== null &&
    getWebPushVapidPublicKeyBase64() !== null
  );
}

/**
 * True when `NEXT_PUBLIC_VAPID_PUBLIC_KEY` is set — enough for W3C Push subscribe + our APIs.
 * (Firebase web config is not required for raw Web Push with VAPID.)
 */
export function isWebPushSubscribeConfigured(): boolean {
  return getWebPushVapidPublicKeyBase64() !== null;
}
